/**
 * The two things a field carries that are about the column rather than the
 * values in it: whether the database indexes it, and whether it refuses
 * duplicates.
 *
 * Pure, and separate from `config.ts` for a reason that shows in the request:
 * these are not properties of a *type*. They sit beside `name` and
 * `description` on every field, and the type only decides whether they are
 * allowed at all.
 *
 * **Both must be sent explicitly whenever the type changes.** A `PATCH` that
 * turns a unique, indexed text field into a checkbox and says nothing about
 * either is refused outright -- `ERROR_INVALID_FIELD_CONSTRAINT` for the
 * constraint the new type cannot have, `ERROR_DB_INDEX_NOT_SUPPORTED` for the
 * index -- so a field with either set could not be changed at all if the dialog
 * left them out. Both confirmed against a running API.
 */
import type { Field, FieldTypeName } from '@/api/types'
import type { FieldProperties } from './config'

/**
 * The only constraint name the API accepts, and the only one there is.
 *
 * The backend registers three constraint types -- `text_type_…`, `rating_type_…`
 * and `generic_…` -- which differ only in the field types they cover and are
 * *rejected* if sent by name: the request carries the shared name below and the
 * backend picks the concrete one from the field's type. So there is one idea
 * here rather than three, and one switch rather than the list of constraints
 * upstream builds, which for every field type has exactly one thing in it.
 */
export const UNIQUE_WITH_EMPTY = 'unique_with_empty'

/** A constraint as the API writes it. */
export interface FieldConstraint {
  type_name: string
}

/**
 * The field types that can refuse duplicates.
 *
 * The backend's own three compatibility lists, joined. Written out and checked
 * against `FieldTypeName`, so a twenty-eighth type has to be answered for
 * rather than silently defaulting to "no".
 */
export const CAN_BE_UNIQUE = {
  text: true,
  long_text: true,
  url: true,
  email: true,
  number: true,
  rating: true,
  boolean: false,
  date: true,
  last_modified: false,
  created_on: false,
  last_modified_by: false,
  created_by: false,
  duration: true,
  link_row: false,
  file: false,
  single_select: true,
  multiple_select: false,
  phone_number: false,
  formula: false,
  count: false,
  rollup: false,
  lookup: false,
  multiple_collaborators: false,
  uuid: false,
  autonumber: false,
  password: false,
  form_view_edit_row: false,
} as const satisfies Record<FieldTypeName, boolean>

/**
 * The field types the database will index.
 *
 * A different list from the one above and not a subset of it either way: a
 * duration or a single select can be unique but cannot be indexed, and a
 * checkbox or a uuid can be indexed but cannot be unique.
 */
export const CAN_BE_INDEXED = {
  text: true,
  long_text: true,
  url: true,
  email: true,
  number: true,
  rating: true,
  boolean: true,
  date: true,
  last_modified: true,
  created_on: true,
  last_modified_by: false,
  created_by: false,
  duration: false,
  link_row: false,
  file: false,
  single_select: false,
  multiple_select: false,
  phone_number: true,
  formula: false,
  count: false,
  rollup: false,
  lookup: false,
  multiple_collaborators: false,
  uuid: true,
  autonumber: true,
  password: false,
  form_view_edit_row: true,
} as const satisfies Record<FieldTypeName, boolean>

/** Whether a field refuses duplicates as it stands. */
export function isUnique(field: Field): boolean {
  const held = (field as { field_constraints?: readonly FieldConstraint[] })
    .field_constraints
  return held !== undefined && held.some((one) => one.type_name === UNIQUE_WITH_EMPTY)
}

/** Whether the database indexes a field as it stands. */
export function isIndexed(field: Field): boolean {
  return (field as { db_index?: boolean }).db_index === true
}

/**
 * What a save sends, narrowed to what the type it is becoming will accept.
 *
 * Narrowed here rather than by resetting the draft, so that changing a unique
 * text field to a checkbox and back leaves it unique. Nothing is lost by
 * carrying a choice the current type cannot honour; something is lost by
 * throwing it away on a keystroke.
 */
export function constraintsToSend(
  type: FieldTypeName,
  unique: boolean,
): FieldConstraint[] {
  return CAN_BE_UNIQUE[type] && unique ? [{ type_name: UNIQUE_WITH_EMPTY }] : []
}

export function indexToSend(type: FieldTypeName, indexed: boolean): boolean {
  return CAN_BE_INDEXED[type] && indexed
}

/**
 * Whether a field's configuration sets a default value for new rows.
 *
 * A default and a uniqueness rule cannot both be set: every row created without
 * a value would take the same one, which is the duplicate the rule exists to
 * refuse. The backend refuses the pair with
 * `ERROR_FIELD_CONSTRAINT_DOES_NOT_SUPPORT_DEFAULT_VALUE`.
 *
 * The property is named for the type -- `text_default`, `number_default` -- which
 * is the backend's own rule rather than a list kept here, and it counts as set
 * when it is truthy, which is the backend's rule too. So a default of `0`, of
 * `false` or of the empty string is no default, and the two sides agree about
 * that without either being told.
 */
export function hasDefaultValue(
  type: FieldTypeName,
  properties: FieldProperties,
): boolean {
  const value = properties[`${type}_default`]
  // An empty list is a default nobody set. Python reads it as false and
  // JavaScript does not, and this is the one shape they disagree about.
  return Array.isArray(value) ? value.length > 0 : Boolean(value)
}

/**
 * Whether the constraint may be changed at all.
 *
 * A field whose values the backend owns, or whose properties something outside
 * this table decides, is refused with `ERROR_IMMUTABLE_FIELD_PROPERTIES` -- but
 * only when the set actually changes, which is why a save that leaves it alone
 * still works on such a field.
 */
export function canChangeConstraints(field: Field): boolean {
  return field.read_only !== true && field.immutable_properties !== true
}
